import React, { useState, useEffect, useRef, useMemo } from "react";
import { Modal, Form, Spinner, Badge, Button } from "react-bootstrap";
import {
  ShoppingCart, Search, ChevronLeft, Plus, Trash2, X, Check, Edit2, WifiOff
} from "lucide-react";
import { useShoppingLists } from "../../hooks/useShoppingLists";
import ShoppingItemTile from "./ShoppingItemTile";
import ItemStagingOverlay from "./ItemStagingOverlay";
import { getItemSuggestions, getGroupColor, getCategoryForItem, getIconForItem } from "./IconMap";
import { CATEGORIES } from "./languages/Categories";
import "./ShoppingLists.css";

const INITIAL_DRAFT = { name: "", quantity: 1, unit: "", propertiesDescription: "" };

const ShoppingListsPage = ({ auth }) => {
  const workspaceId = localStorage.getItem("workspaceId");
  const token = auth.user?.id_token;

  const {
    lists, activeList, activeListId, setActiveListId,
    suggestions, recentlyUsed, loading, addItem, updateItem, markPurchased,
    createList, deleteList, renameList, error, setError
  } = useShoppingLists({ workspaceId, token });

  const [isCreatingList, setIsCreatingList] = useState(false);
  const [newListTitle, setNewListTitle] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [itemDraft, setItemDraft] = useState(INITIAL_DRAFT);
  const [localSuggestions, setLocalSuggestions] = useState([]);
  const [stagedItem, setStagedItem] = useState(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameTitle, setRenameTitle] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Offline capability
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Keyboard Navigation State
  const inputRef = useRef(null);
  const [focusedSuggestionIndex, setFocusedSuggestionIndex] = useState(-1);

  /* 
   * INTERACTION LOCK:
   * Prevents accidental clicks on items that "slide up" under the cursor 
   * immediately after an item is removed (optimistic update).
   */
  const [interactionLock, setInteractionLock] = useState(false);

  const handleItemClick = (itemId) => {
    if (interactionLock) return;

    setInteractionLock(true);
    markPurchased(itemId);

    // Lock interactions for 400ms to allow layout to settle
    setTimeout(() => {
      setInteractionLock(false);
    }, 400);
  };

  useEffect(() => {
    if (activeList) {
      setRenameTitle(activeList.name);
    }
  }, [activeList]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [error, setError]);

  const handleNameChange = (e) => {
    const val = e.target.value;
    setItemDraft({ ...itemDraft, name: val });
    if (val.length >= 2) {
      setLocalSuggestions(getItemSuggestions(val));
    } else {
      setLocalSuggestions([]);
    }
    setFocusedSuggestionIndex(-1);
  };

  // Updates the currently staged item in the backend (e.g. quantity/unit changes)
  const updateStagedItem = async (itemToUpdate = stagedItem) => {
    if (!itemToUpdate || !itemToUpdate.id) return;
    await updateItem(itemToUpdate.id, {
      ...itemToUpdate,
      quantity: parseFloat(itemToUpdate.quantity) || 1
    });
    setStagedItem(null);
  };

  const handleAddItem = async (overrideItem = null) => {
    // 1. If we have a staged item, save its updates first (Continuous Entry)
    if (stagedItem) {
      await updateStagedItem();
    }

    // 2. Prepare the new item
    const baseItem = overrideItem ? { ...INITIAL_DRAFT, ...overrideItem } : itemDraft;
    if (!baseItem.name.trim()) return;

    // Normalize name: trim and capitalize first letter
    const normalizedName = baseItem.name.trim().charAt(0).toUpperCase() + baseItem.name.trim().slice(1);

    // Check for duplicates in current list
    const existingItem = activeList?.items?.find(i => i.name.toLowerCase() === normalizedName.toLowerCase());

    if (existingItem) {
      // Update quantity of existing item
      const quantityToAdd = parseFloat(baseItem.quantity) || 1;
      const newQuantity = (parseFloat(existingItem.quantity) || 0) + quantityToAdd;

      await updateItem(existingItem.id, {
        ...existingItem,
        quantity: newQuantity
      });

      // Stage the updated item so user sees it happened
      setStagedItem({
        ...existingItem,
        quantity: newQuantity,
        tmpId: Date.now()
      });

    } else {
      // 3. Add immediately to backend to get an ID and "check" the suggestion
      const newItem = await addItem({
        ...baseItem,
        name: normalizedName,
        quantity: parseFloat(baseItem.quantity) || 1
      });

      if (newItem) {
        // 4. Stage the new item for further editing (e.g. quantity/unit)
        const icon = getIconForItem(newItem.name);
        setStagedItem({
          ...newItem,
          icon: icon || newItem.icon,
          tmpId: Date.now() // Force re-render of animation
        });
      }
    }

    // 5. Clear input
    if (!overrideItem) {
      setItemDraft(INITIAL_DRAFT);
      setLocalSuggestions([]);
    }
    setFocusedSuggestionIndex(-1);

    // Auto-refocus the input for continuous entry flow
    setTimeout(() => {
      inputRef.current?.focus();
    }, 10);
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;
    await updateItem(editingItem.id, {
      ...editingItem,
      quantity: parseFloat(editingItem.quantity)
    });
    setEditingItem(null);
  };

  const submitNewList = async () => {
    if (newListTitle.trim()) {
      await createList(newListTitle);
      setNewListTitle("");
    }
    setIsCreatingList(false);
  };

  const handleRenameList = async () => {
    if (renameTitle.trim() && activeList) {
      await renameList(activeList.id, renameTitle);
      setIsRenaming(false);
    }
  };

  const confirmDeleteList = async () => {
    if (activeList) {
      await deleteList(activeList.id);
      setShowDeleteModal(false);
      // Wait for delete list to update view? deleteList hook handles list removal from state.
    }
  };

  // Compute visible chips for quick add (similar to Android horizontal chip rows)
  const visibleChips = useMemo(() => {
    if (localSuggestions.length > 0) return localSuggestions;
    const recents = recentlyUsed.slice(0, 8).map(r => ({ ...r, type: 'recent' }));

    const recentNames = new Set(recents.map(r => r.name.toLowerCase()));
    const suggs = suggestions
      .filter(s => !recentNames.has(s.name.toLowerCase()))
      .slice(0, 8)
      .map(s => ({ ...s, type: 'suggestion' }));

    // Attach default icons if missing
    return [...recents, ...suggs].map(item => ({
      ...item,
      icon: item.icon || getIconForItem(item.name)
    }));
  }, [localSuggestions, recentlyUsed, suggestions]);

  // Handle Keyboard Navigation within Input
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (focusedSuggestionIndex >= 0 && focusedSuggestionIndex < visibleChips.length) {
        handleAddItem({ name: visibleChips[focusedSuggestionIndex].name });
      } else {
        handleAddItem();
      }
    } else if (e.key === "ArrowRight") {
      if (focusedSuggestionIndex < visibleChips.length - 1) {
        e.preventDefault();
        setFocusedSuggestionIndex(prev => prev + 1);
      }
    } else if (e.key === "ArrowLeft") {
      if (focusedSuggestionIndex > 0) {
        e.preventDefault();
        setFocusedSuggestionIndex(prev => prev - 1);
      } else if (focusedSuggestionIndex === 0) {
        e.preventDefault();
        setFocusedSuggestionIndex(-1);
      }
    } else if (e.key === "Tab") {
      if (focusedSuggestionIndex >= 0 && focusedSuggestionIndex < visibleChips.length) {
        e.preventDefault();
        handleAddItem({ name: visibleChips[focusedSuggestionIndex].name });
      }
    }
  };


  if (loading && lists.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100 bg-light">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="d-flex flex-column flex-md-row shadow-sm flex-grow-1 page-container">
      {/* SIDEBAR */}
      <aside className={`flex-column border-end bg-white transition-all ${activeListId ? 'd-none d-md-flex' : 'd-flex'}`} style={{ width: "100%", maxWidth: "350px", overflow: "hidden" }}>
        <div className="p-3">
          <h5 className="fw-bold mb-3 d-none d-md-block">Einkaufslisten</h5>
          <div className="position-relative mb-2">
            <Search className="position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" size={16} />
            <Form.Control placeholder="Suchen..." className="ps-5 border-0 bg-light" style={{ borderRadius: "8px" }} />
          </div>
        </div>

        <div className="flex-grow-1 overflow-auto px-2 pb-5">
          {lists.map(l => (
            <div
              key={l.id}
              onClick={() => setActiveListId(l.id)}
              className={`p-3 rounded-3 cursor-pointer mb-2 border-start border-4 transition-all d-flex justify-content-between align-items-center ${activeListId === l.id ? 'bg-light border-primary shadow-sm' : 'border-transparent'
                }`}
            >
              <div className="text-truncate" style={{ maxWidth: '80%' }}>
                <div className="fw-bold small text-truncate">{l.name}</div>
              </div>
              <Badge pill bg={activeListId === l.id ? "primary" : "secondary"} className="opacity-75">
                {l.items?.length || 0}
              </Badge>
            </div>
          ))}

          {isCreatingList ? (
            <div className="p-2 bg-white rounded-3 shadow-sm border mb-2 mx-1">
              <Form.Control
                autoFocus size="sm" placeholder="Name der Einkaufsliste..."
                className="border-0 mb-2 shadow-none"
                value={newListTitle}
                onChange={(e) => setNewListTitle(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitNewList()}
              />
              <div className="d-flex justify-content-end gap-1">
                <Button variant="light" size="sm" onClick={() => setIsCreatingList(false)}><X size={14} /></Button>
                <Button variant="primary" size="sm" onClick={submitNewList}><Check size={14} /></Button>
              </div>
            </div>
          ) : (
            <Button variant="link" className="w-100 text-primary text-decoration-none mt-2 d-flex align-items-center justify-content-center gap-2 py-2 rounded-3" onClick={() => setIsCreatingList(true)}>
              <Plus size={18} /> Neue Liste erstellen
            </Button>
          )}
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main className={`flex-grow-1 d-flex flex-column h-100 position-relative ${!activeListId ? 'd-none d-md-flex' : ''}`} style={{ overflow: "hidden" }}>
        {activeList ? (
          <>
            {/* HEADER */}
            <div className="px-3 px-md-4 py-3 border-bottom d-flex align-items-center justify-content-between bg-white shadow-sm flex-shrink-0">
              <div className="d-flex align-items-center gap-2 overflow-hidden">
                <Button variant="light" size="sm" className="d-md-none rounded-circle p-1" onClick={() => setActiveListId(null)}>
                  <ChevronLeft size={20} />
                </Button>
                <div className="text-truncate d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                  {isRenaming ? (
                    <div className="d-flex align-items-center gap-2">
                      <Form.Control
                        autoFocus
                        value={renameTitle}
                        onChange={(e) => setRenameTitle(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleRenameList()}
                        size="sm"
                      />
                      <Button variant="success" size="sm" onClick={handleRenameList}><Check size={16} /></Button>
                      <Button variant="light" size="sm" onClick={() => setIsRenaming(false)}><X size={16} /></Button>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center gap-2 text-truncate">
                      <h4 className="fw-bold mb-0 text-truncate" onClick={() => { setIsRenaming(true); setRenameTitle(activeList.name); }} style={{ cursor: "pointer" }}>
                        {activeList.name}
                      </h4>
                      <Button variant="ghost" size="sm" className="p-0 text-muted opacity-50 hover-opacity-100" onClick={() => { setIsRenaming(true); setRenameTitle(activeList.name); }}>
                        <Edit2 size={14} />
                      </Button>
                    </div>
                  )}

                </div>
              </div>
              {!isRenaming && (
                <Button variant="outline-danger" size="sm" className="rounded-circle p-2 border-0"
                  onClick={() => setShowDeleteModal(true)}>
                  <Trash2 size={18} />
                </Button>
              )}
            </div>

            {/* CONTENT WRAPPER */}
            <div className="d-flex flex-column flex-grow-1 bg-white overflow-hidden position-relative">

              {/* OFFLINE BANNER */}
              {isOffline && (
                <div className="offline-banner">
                  <WifiOff size={16} className="flex-shrink-0" />
                  <span>Kein Internet – Änderungen werden synchronisiert, sobald du wieder online bist.</span>
                </div>
              )}

              {/* TOP INPUT BAR (Unified layout for desktop & mobile, placed directly above items) */}
              <div className="top-input-bar border-bottom p-3 shadow-sm bg-white">
                <div className="mx-auto w-100" style={{ maxWidth: "800px" }}>

                  {/* Staging Panel (Inline) */}
                  <ItemStagingOverlay
                    stagedItem={stagedItem}
                    onSave={() => updateStagedItem()}
                    onCancel={() => setStagedItem(null)}
                    onChange={setStagedItem}
                  />

                  {/* QUICK ADD / SUGGESTION STRIP */}
                  {visibleChips.length > 0 && (
                    <div className="d-flex gap-2 overflow-auto pb-2 mb-2 suggestion-strip" style={{ whiteSpace: 'nowrap' }}>
                      {visibleChips.map((chip, idx) => {
                        const exists = activeList.items?.some(i => i.name.toLowerCase() === chip.name.toLowerCase());
                        const hasImage = chip.imagePath || (chip.icon && (chip.icon.includes('/') || /\.(png|jpg|jpeg|webp|svg)$/.test(chip.icon)));

                        return (
                          <div
                            key={idx}
                            className={`quick-add-chip ${focusedSuggestionIndex === idx ? 'focused' : ''} ${exists ? 'bg-success-subtle border-success text-success' : 'hover-bg-gray'}`}
                            onClick={() => {
                              if (exists) return;
                              handleAddItem({ name: chip.name });
                              setFocusedSuggestionIndex(-1);
                            }}
                          >
                            {hasImage && (
                              <img src={chip.imagePath || chip.icon} alt="" style={{ width: '18px', height: '18px', objectFit: 'contain' }} />
                            )}
                            <span>{chip.name}</span>
                            {exists && <Check size={14} />}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* INPUT PILL */}
                  <div className="input-pill-container border">
                    <Form.Control
                      ref={inputRef}
                      value={itemDraft.name}
                      onChange={handleNameChange}
                      onKeyDown={handleKeyDown}
                      placeholder="Ich brauche..."
                      autoFocus
                    />
                    <Button variant="primary" onClick={() => handleAddItem()} className="rounded-circle d-flex align-items-center justify-content-center p-0" style={{ width: '32px', height: '32px', flexShrink: 0 }}>
                      <Plus size={20} />
                    </Button>
                  </div>
                </div>
              </div>

              {/* LIST CONTENT */}
              <div className="flex-grow-1 overflow-auto p-3 p-md-4 scrollable-list-area" id="shopping-list-container">
                <div className="mx-auto w-100" style={{ maxWidth: "800px" }}>

                  {/* EXISTING ERROR MSG */}
                  {error && <div className="alert alert-warning py-2 mb-3 shadow-sm border-0">{error}</div>}

                  <h6 className="text-muted fw-bold small mb-3">AKTUELL BENÖTIGT ({activeList.items?.length || 0})</h6>
                  {activeList.items?.length === 0 ? (
                    <div className="text-center py-5 text-muted opacity-50">
                      <ShoppingCart size={48} className="mb-3" />
                      <p>Deine Liste ist leer.<br />Füge oben Artikel hinzu.</p>
                    </div>
                  ) : (
                    <div className="tile-grid mb-5">
                      {activeList.items
                        ?.slice()
                        .sort((a, b) => {
                          const catKeyA = getCategoryForItem(a.name);
                          const catKeyB = getCategoryForItem(b.name);
                          const orderA = (catKeyA && CATEGORIES[catKeyA]) ? CATEGORIES[catKeyA].sortOrder : 999;
                          const orderB = (catKeyB && CATEGORIES[catKeyB]) ? CATEGORIES[catKeyB].sortOrder : 999;
                          if (orderA !== orderB) return orderA - orderB;
                          return a.name.localeCompare(b.name);
                        })
                        .map(item => (
                          <ShoppingItemTile
                            key={item.id}
                            item={item}
                            color={getGroupColor(item.name)}
                            onClick={() => handleItemClick(item.id)}
                            onLongPress={setEditingItem}
                          />
                        ))}
                    </div>
                  )}
                </div>
              </div>

            </div>
          </>
        ) : (
          <div className="m-auto text-center text-muted">
            <ShoppingCart size={48} className="mb-3 opacity-25" />
            <h5>Wähle eine Einkaufsliste</h5>
          </div>
        )}
      </main>

      {/* EDIT MODAL */}
      <Modal show={!!editingItem} onHide={() => setEditingItem(null)} centered size="sm">
        <Modal.Header closeButton className="border-0"><Modal.Title className="h6 fw-bold">Bearbeiten</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="small text-muted">Name</Form.Label>
            <Form.Control
              value={editingItem?.name || ""}
              onChange={e => setEditingItem({ ...editingItem, name: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && handleSaveEdit()}
            />
          </Form.Group>
          <div className="d-flex gap-2 mb-3">
            <Form.Group>
              <Form.Label className="small text-muted">Menge</Form.Label>
              <Form.Control
                type="number"
                value={editingItem?.quantity || ""}
                onChange={e => setEditingItem({ ...editingItem, quantity: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && handleSaveEdit()}
              />
            </Form.Group>
            <Form.Group className="flex-grow-1">
              <Form.Label className="small text-muted">Einheit</Form.Label>
              <Form.Control
                value={editingItem?.unit || ""}
                onChange={e => setEditingItem({ ...editingItem, unit: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && handleSaveEdit()}
              />
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="light" onClick={() => setEditingItem(null)}>Abbrechen</Button>
          <Button variant="primary" onClick={handleSaveEdit}>Speichern</Button>
        </Modal.Footer>
      </Modal>

      {/* DELETE MODAL */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered size="sm">
        <Modal.Header closeButton className="border-0"><Modal.Title className="h6 fw-bold">Liste löschen</Modal.Title></Modal.Header>
        <Modal.Body className="text-center py-4">
          <p className="mb-0">Möchten Sie die Liste <strong>{activeList?.name}</strong> wirklich löschen?</p>
          <small className="text-muted">Dies kann nicht rückgängig gemacht werden.</small>
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-center">
          <Button variant="light" onClick={() => setShowDeleteModal(false)} className="px-4">Abbrechen</Button>
          <Button variant="danger" onClick={confirmDeleteList} className="px-4">Löschen</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ShoppingListsPage;